import { useClient } from '../supabase/client'
import { useGetJwt } from '../supabase/get-jwt'

import { useMutation } from '@tanstack/vue-query'
import imageCompression from 'browser-image-compression'
import dayjs from 'dayjs'

export type UploadMutationProps = {
   objects: File | FileList
   path: string
}

export const useUploadToBucket = () => {
   const jwt = useGetJwt()
   const client = useClient()

   return useMutation({
      mutationFn: async (props: UploadMutationProps) => {
         try {
            const files =
               props.objects instanceof FileList ? Array.from(props.objects) : [props.objects]
            const [bucket, ...path] = props.path.split('/')

            const uploadPromises = files.map(async (file, index) => {
               const fileName = `${path.join('/')}/${Number(dayjs())}-${
                  jwt.value?.user_details?.id || 'anon'
               }-${index}-${file.name}`
               let fileToUpload: File | Blob = file
               let contentType = file.type

               if (file.type?.startsWith('image/')) {
                  fileToUpload = await imageCompression(file, {
                     fileType: 'image/jpeg',
                     initialQuality: 0.7,
                  })
                  contentType = 'image/jpeg'
               }

               const { error, data } = await client.storage
                  .from(bucket)
                  .upload(fileName.replace(/[^a-zA-Z0-9/_.-]/g, ''), fileToUpload, {
                     contentType,
                  })

               if (error) {
                  throw new Error(error.message)
               }

               return `${bucket}/${data.path}`
            })

            const results = await Promise.all(uploadPromises)
            return results
         } catch (e: any) {
            if (e?.message.includes('exceeded the maximum')) {
               throw new Error('error.api.upload-size')
            }

            throw new Error('error.api.upload')
         }
      },
   })
}
